import React, { useState, useEffect, useMemo } from 'react';
import { BottomSheet } from '../../../shared/ui';
import { SessionV2, sessionsV2Api, SessionActivity } from '../../../api/sessionsV2Api';
import { sitePaymentAllocationV2Api, UnpaidEarningForAllocationV2, ShiftPaymentAllocationPreviewResponseV2, ShiftPaymentAllocationRequestV2, getPaymentAllocationConflictMessage } from '../../../api/sitePaymentAllocationV2Api';
import { paymentsV2Api, PaymentV2 } from '../../../api/paymentsV2Api';
import { useToast } from '../../../shared/contexts/ToastContext';
import './MultipleSessionsPaymentBottomSheet.css';

interface MultipleSessionsPaymentBottomSheetProps {
  isOpen: boolean;
  sessions: SessionV2[];
  siteId: number;
  onClose: () => void;
  onSuccess?: () => void;
  typeOfWorkId?: number; // Фильтр по виду работы
  isAdditionalWork?: boolean; // Фильтр по доп.работе: true — только доп, false — только не доп
  /** Предзагруженные активности из карточки — позволяет открыть окно без дополнительной загрузки */
  preloadedActivities?: Record<number, SessionActivity[]>;
}

interface SessionWithActivities extends SessionV2 {
  activities: SessionActivity[];
}

interface ActivityWithAllocation extends SessionActivity {
  allocationAmount?: number;
  sessionId: number;
  sessionUserName?: string;
}

interface PaymentWithRemaining extends PaymentV2 {
  remainingAmount: number;
}

/** Суммирование денег в копейках — как на сервере (без float-дрейфа). */
function sumMoneyRub(values: number[]): number {
  const kopeks = values.reduce((sum, value) => sum + Math.round(value * 100), 0);
  return kopeks / 100;
}

function roundMoneyRub(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

const MultipleSessionsPaymentBottomSheet: React.FC<MultipleSessionsPaymentBottomSheetProps> = ({
  isOpen,
  sessions,
  siteId,
  onClose,
  onSuccess,
  typeOfWorkId, // Фильтр по виду работы
  isAdditionalWork, // Фильтр по доп.работе
  preloadedActivities // Предзагруженные из карточки — окно открывается мгновенно
}) => {
  const MIN_AMOUNT = 0.01;
  const [sessionsWithActivities, setSessionsWithActivities] = useState<SessionWithActivities[]>([]);
  const [loading, setLoading] = useState(false);
  const [newPaymentAmount, setNewPaymentAmount] = useState<string>('');
  const [newPaymentComment, setNewPaymentComment] = useState<string>('');
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<Set<number>>(new Set());
  const [availablePayments, setAvailablePayments] = useState<PaymentWithRemaining[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [unpaidEarnings, setUnpaidEarnings] = useState<UnpaidEarningForAllocationV2[]>([]);
  const [payUnpaidEarnings, setPayUnpaidEarnings] = useState(true);
  const [createNewPaymentForRemaining, setCreateNewPaymentForRemaining] = useState(false);
  const [serverPreview, setServerPreview] = useState<ShiftPaymentAllocationPreviewResponseV2 | null>(null);
  const [isNewPaymentExpanded, setIsNewPaymentExpanded] = useState(false);
  /** Свернутые сессии по дате (dateKey) — для компактности */
  const [collapsedSessionDates, setCollapsedSessionDates] = useState<Set<string>>(new Set());
  const { showError, showSuccess } = useToast();

  const toggleSessionCollapsed = (dateKey: string) => {
    setCollapsedSessionDates(prev => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  };

  // Проверяем, что все смены принадлежат одному исполнителю (userId или siteExecutorId)
  const isSingleUser = useMemo(() => {
    if (sessions.length === 0) return true;
    const executorIds = new Set(
      sessions
        .map(s => s.siteExecutorId)
        .filter((id): id is number => id != null && id > 0)
    );
    if (executorIds.size > 0) return executorIds.size === 1;
    const userIds = new Set(
      sessions
        .map(s => s.userId)
        .filter((id): id is number => id != null && id > 0)
    );
    return userIds.size <= 1;
  }, [sessions]);

  const resolvedSiteExecutorId = useMemo(() => {
    const executorIds = Array.from(new Set(
      sessions
        .map(s => s.siteExecutorId)
        .filter((id): id is number => id != null && id > 0)
    ));
    return executorIds.length === 1 ? executorIds[0] : null;
  }, [sessions]);

  const resolvedUserId = useMemo(() => {
    if (resolvedSiteExecutorId != null) return null;
    const userIds = Array.from(new Set(
      sessions
        .map(s => s.userId)
        .filter((id): id is number => id != null && id > 0)
    ));
    return userIds.length === 1 ? userIds[0] : null;
  }, [sessions, resolvedSiteExecutorId]);

  const userId = resolvedUserId;
  const siteExecutorId = resolvedSiteExecutorId;

  const allocationTarget = useMemo(() => {
    if (siteExecutorId != null) return { siteExecutorId };
    if (userId != null) return { userId };
    return {};
  }, [siteExecutorId, userId]);

  const allocationTargetReady = useMemo(() => {
    return siteExecutorId != null || userId != null;
  }, [sessions]);

  const userName = useMemo(() => {
    return sessions.length > 0 ? sessions[0].userName || `Исполнитель ${sessions[0].siteExecutorId ?? sessions[0].userId}` : '';
  }, [sessions]);

  // Вычисляем общую рекомендованную сумму (сумма всех неоплаченных активностей)
  const recommendedAmount = useMemo(() => {
    const remainings = sessionsWithActivities.flatMap(session =>
      session.activities
        .filter(a => a.hasFinancialAccess && (a.remainingAmount ?? 0) > MIN_AMOUNT)
        .map(a => a.remainingAmount || 0)
    );
    return sumMoneyRub(remainings);
  }, [sessionsWithActivities]);

  const selectedSessionIdsSet = useMemo(() => new Set(sessions.map(s => s.id)), [sessions]);

  const unpaidBonusEarningsForSelection = useMemo(() => {
    // Bonus (2), Manual (5), Compensation (6) — все типы которые платятся как начисления
    const allPayable = unpaidEarnings.filter(e => e.earningTypeId === 2 || e.earningTypeId === 5 || e.earningTypeId === 6);
    const bySelectedSessions = allPayable.filter(e =>
      selectedSessionIdsSet.has(e.sessionId) || e.sessionId === 0
    );

    // Fallback: если привязка к сессиям не восстановилась на сервере,
    // показываем все неоплаченные начисления исполнителя.
    return bySelectedSessions.length > 0 ? bySelectedSessions : allPayable;
  }, [unpaidEarnings, selectedSessionIdsSet]);

  const unpaidBonusEarningsTotal = useMemo(() => {
    return sumMoneyRub(unpaidBonusEarningsForSelection.map(e => e.remaining));
  }, [unpaidBonusEarningsForSelection]);

  // Вычисляем сумму из выбранных нераспределенных платежей
  const selectedUnallocatedAmount = useMemo(() => {
    return sumMoneyRub(
      availablePayments
        .filter(p => selectedPaymentIds.has(p.id))
        .map(p => p.remainingAmount)
    );
  }, [availablePayments, selectedPaymentIds]);

  // Общая цель оплаты: зарплата по активностям + (опционально) неоплаченные премии
  const targetAmount = useMemo(() => {
    return recommendedAmount + (payUnpaidEarnings ? unpaidBonusEarningsTotal : 0);
  }, [recommendedAmount, payUnpaidEarnings, unpaidBonusEarningsTotal]);

  // Вычисляем рекомендуемую доплату (локальный fallback до загрузки preview)
  const recommendedTopUp = useMemo(() => {
    return roundMoneyRub(Math.max(0, targetAmount - selectedUnallocatedAmount));
  }, [targetAmount, selectedUnallocatedAmount]);

  const displayRecommendedTopUp = serverPreview?.recommendedTopUp ?? recommendedTopUp;

  const buildShiftPaymentRequest = (): ShiftPaymentAllocationRequestV2 => ({
    userId: userId ?? undefined,
    siteExecutorId: siteExecutorId ?? undefined,
    sessionIds: sessions.map(s => s.id),
    selectedPaymentIds: Array.from(selectedPaymentIds),
    newPaymentAmount: createNewPaymentForRemaining ? 0 : (parseFloat(newPaymentAmount || '0') || 0),
    createNewPaymentForRemaining,
    newPaymentComment: newPaymentComment || undefined,
    payUnpaidEarnings: payUnpaidEarnings && unpaidBonusEarningsTotal > 0.01,
    selectedEarningIds: payUnpaidEarnings
      ? unpaidBonusEarningsForSelection.map(e => e.id)
      : []
  });

  // Общая сумма для распределения (деньги: нераспределённые + новый платёж)
  const totalAmount = useMemo(() => {
    if (createNewPaymentForRemaining && serverPreview?.success) {
      return roundMoneyRub(selectedUnallocatedAmount + serverPreview.computedNewPaymentAmount);
    }
    const newAmount = parseFloat(newPaymentAmount || '0');
    return selectedUnallocatedAmount + (isNaN(newAmount) ? 0 : newAmount);
  }, [selectedUnallocatedAmount, newPaymentAmount, createNewPaymentForRemaining, serverPreview]);

  // Получаем все неоплаченные активности всех смен
  const allUnpaidActivities = useMemo((): ActivityWithAllocation[] => {
    const activities: ActivityWithAllocation[] = [];
    
    sessionsWithActivities.forEach(session => {
      session.activities
        .filter(a => a.hasFinancialAccess && (a.remainingAmount ?? 0) > MIN_AMOUNT)
        .forEach(activity => {
          activities.push({
            ...activity,
            sessionId: session.id,
            sessionUserName: session.userName
          });
        });
    });

    return activities.sort((a, b) => 
      (a.timeStart ? new Date(a.timeStart).getTime() : 0) - (b.timeStart ? new Date(b.timeStart).getTime() : 0)
    );
  }, [sessionsWithActivities]);

  // Вычисляем распределение средств по активностям
  const activityAllocations = useMemo((): ActivityWithAllocation[] => {
    if (allUnpaidActivities.length === 0 || totalAmount <= MIN_AMOUNT) return [];

    let remainingAmount = totalAmount;
    const allocations: ActivityWithAllocation[] = [];

    for (const activity of allUnpaidActivities) {
      if (remainingAmount <= MIN_AMOUNT) break;

      const activityRemaining = activity.remainingAmount || 0;
      const allocationAmount = Math.min(activityRemaining, remainingAmount);

      if (allocationAmount > MIN_AMOUNT) {
        allocations.push({
          ...activity,
          allocationAmount
        });
        remainingAmount -= allocationAmount;
      } else {
        break;
      }
    }

    return allocations;
  }, [allUnpaidActivities, totalAmount]);

  /** Группировка активностей по дате (сессия = день) для отображения */
  const activityAllocationsByDate = useMemo(() => {
    if (activityAllocations.length === 0) return [];
    const map = new Map<string, ActivityWithAllocation[]>();
    for (const a of activityAllocations) {
      const dateKey = a.timeStart ? new Date(a.timeStart).toISOString().slice(0, 10) : 'unknown';
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey)!.push(a);
    }
    const sortedKeys = Array.from(map.keys()).sort();
    const formatDateKeyLabel = (key: string) => {
      if (key === 'unknown') return 'Дата не указана';
      const dt = new Date(`${key}T00:00:00`);
      if (Number.isNaN(dt.getTime())) return 'Дата не указана';
      return dt.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        weekday: 'short'
      });
    };
    return sortedKeys.map(dateKey => ({
      dateKey,
      dateLabel: formatDateKeyLabel(dateKey),
      activities: map.get(dateKey)!,
      sessionSum: map.get(dateKey)!.reduce((s, x) => s + (x.allocationAmount ?? 0), 0)
    }));
  }, [activityAllocations]);

  // По умолчанию все сессии свёрнуты — при первой загрузке данных сворачиваем все
  useEffect(() => {
    if (!isOpen || activityAllocationsByDate.length === 0) return;
    setCollapsedSessionDates(prev => {
      const allKeys = new Set(activityAllocationsByDate.map(d => d.dateKey));
      if (prev.size === 0) return allKeys;
      return prev;
    });
  }, [isOpen, activityAllocationsByDate]);

  // Вычисляем сумму распределения
  const allocatedAmount = useMemo(() => {
    return sumMoneyRub(activityAllocations.map(a => a.allocationAmount || 0));
  }, [activityAllocations]);

  // Вычисляем нераспределенную сумму (переплата)
  const unallocatedAmount = useMemo(() => {
    return Math.max(0, totalAmount - allocatedAmount);
  }, [totalAmount, allocatedAmount]);

  // Сколько из остатка после зарплаты пойдет на оплату премиальных начислений
  const plannedPremiumAllocation = useMemo(() => {
    if (!payUnpaidEarnings) return 0;
    return Math.min(unpaidBonusEarningsTotal, unallocatedAmount);
  }, [payUnpaidEarnings, unpaidBonusEarningsTotal, unallocatedAmount]);

  // Фактически нераспределенный остаток после учета оплаты премий
  const effectiveUnallocatedAmount = useMemo(() => {
    return Math.max(0, unallocatedAmount - plannedPremiumAllocation);
  }, [unallocatedAmount, plannedPremiumAllocation]);

  // Общее планируемое распределение (зарплата + премии)
  const plannedTotalAllocation = useMemo(() => {
    return allocatedAmount + plannedPremiumAllocation;
  }, [allocatedAmount, plannedPremiumAllocation]);

  const hasPremiumOnlyDistribution = useMemo(() => {
    return activityAllocations.length === 0 && payUnpaidEarnings && unpaidBonusEarningsTotal > 0.01;
  }, [activityAllocations.length, payUnpaidEarnings, unpaidBonusEarningsTotal]);

  // Загружаем активности для всех смен (или используем предзагруженные — окно открывается мгновенно, данные догружаются внутри)
  useEffect(() => {
    if (!isOpen || sessions.length === 0) {
      setSessionsWithActivities([]);
      setNewPaymentAmount('');
      setNewPaymentComment('');
      setSelectedPaymentIds(new Set());
      setCreateNewPaymentForRemaining(false);
      setServerPreview(null);
      setIsNewPaymentExpanded(false);
      setCollapsedSessionDates(new Set());
      setLoading(false);
      return;
    }

    const applyFilters = (activities: SessionActivity[]) => {
      let filtered = activities;
      if (typeOfWorkId !== undefined) filtered = filtered.filter(a => a.typeOfWorkId === typeOfWorkId);
      if (isAdditionalWork === true) filtered = filtered.filter(a => a.isAdditionalWork === true);
      else if (isAdditionalWork === false) filtered = filtered.filter(a => a.isAdditionalWork !== true);
      return filtered;
    };

    const hasPreloaded = preloadedActivities && sessions.every(s => preloadedActivities[s.id] !== undefined);

    // Предзагруженные активности — контент сразу, без спиннера
    if (hasPreloaded) {
      const data = sessions.map(s => ({ ...s, activities: applyFilters(preloadedActivities![s.id] || []) }));
      setSessionsWithActivities(data);
      setLoading(false);
      return;
    }

    // Нет предзагрузки — сразу показываем загрузку, данные подтянутся в процессе
    setLoading(true);

    const loadAllActivities = async () => {
      try {
        // Пытаемся использовать batch API для оптимизации
        const sessionIds = sessions.map(s => s.id);
        
        try {
          console.log(`🚀 Используем BATCH API для загрузки активностей ${sessionIds.length} смен` + (typeOfWorkId ? ` с фильтром typeOfWorkId=${typeOfWorkId}` : ''));
          const activitiesMap = await sessionsV2Api.getBatchSessionActivities(siteId, sessionIds);
          
          const sessionsWithActivitiesData = sessions.map(session => {
            let activities = activitiesMap.get(session.id) || [];
            
            // Применяем фильтр по виду работы, если он указан
            if (typeOfWorkId !== undefined) {
              activities = activities.filter(a => a.typeOfWorkId === typeOfWorkId);
            }
            // Применяем фильтр по доп.работе (как в карточке смен)
            if (isAdditionalWork === true) {
              activities = activities.filter(a => a.isAdditionalWork === true);
            } else if (isAdditionalWork === false) {
              activities = activities.filter(a => a.isAdditionalWork !== true);
            }
            
            return { ...session, activities };
          });
          
          setSessionsWithActivities(sessionsWithActivitiesData);
          console.log(`✅ BATCH API: успешно загружены активности для ${sessionIds.length} смен` + (typeOfWorkId ? ` (с фильтром)` : ''));
        } catch (batchError: any) {
          // Fallback на старый метод, если batch API недоступен
          console.warn('⚠️ Batch API недоступен, используем fallback метод:', batchError);
          
          if (batchError.response?.status === 404 || batchError.response?.status === 501) {
            console.log(`🔄 Загружаем активности по одной смене (${sessionIds.length} запросов)` + (typeOfWorkId ? ` с фильтром typeOfWorkId=${typeOfWorkId}` : ''));
            const sessionsWithActivitiesData = await Promise.all(
              sessions.map(async (session) => {
                try {
                  const activities = await sessionsV2Api.getSessionActivities(siteId, session.id, typeOfWorkId);
                  let filtered = activities;
                  if (isAdditionalWork === true) {
                    filtered = activities.filter(a => a.isAdditionalWork === true);
                  } else if (isAdditionalWork === false) {
                    filtered = activities.filter(a => a.isAdditionalWork !== true);
                  }
                  return { ...session, activities: filtered };
                } catch (error) {
                  console.error(`Error loading activities for session ${session.id}:`, error);
                  return { ...session, activities: [] };
                }
              })
            );
            setSessionsWithActivities(sessionsWithActivitiesData);
            console.log(`✅ Fallback: успешно загружены активности для ${sessionIds.length} смен` + (typeOfWorkId ? ` (с фильтром)` : ''));
          } else {
            throw batchError;
          }
        }
      } catch (error: any) {
        console.error('❌ Error loading activities:', error);
        showError('Ошибка загрузки активностей');
      } finally {
        setLoading(false);
      }
    };

    loadAllActivities();
  }, [isOpen, sessions, siteId, showError, typeOfWorkId, isAdditionalWork, preloadedActivities]);

  // Загружаем доступные платежи с нераспределенными средствами
  // executorKey: userId или siteExecutorId — окно оплаты работает и для сотрудников, и для исполнителей
  const executorKey = siteExecutorId ?? userId ?? null;
  useEffect(() => {
    if (!isOpen || executorKey == null) {
      setAvailablePayments([]);
      setUnpaidEarnings([]);
      return;
    }

    const loadPayments = async () => {
      setLoadingPayments(true);
      try {
        const paymentsWithRemaining = await paymentsV2Api.getSitePaymentsWithRemaining(
          siteId, 
          allocationTarget
        );
        
        // Сортируем по дате (новые сначала)
        paymentsWithRemaining.sort((a, b) => 
          new Date(b.date).getTime() - new Date(a.date).getTime()
        );

        setAvailablePayments(paymentsWithRemaining);
      } catch (error: any) {
        console.error('Error loading payments:', error);
        // Если новый метод недоступен (404), используем старый подход
        if (error.response?.status === 404 || error.response?.status === 501) {
          console.warn('Using fallback method for loading payments');
          try {
            const payments = await paymentsV2Api.getSitePayments(siteId, allocationTarget);

            const paymentDetailsPromises = payments.map(async (payment) => {
              try {
                const details = await sitePaymentAllocationV2Api.getPaymentAllocations(siteId, payment.id);
                if (details.payment.remainingAmount > 0.01) {
                  return {
                    ...payment,
                    remainingAmount: details.payment.remainingAmount
                  };
                }
                return null;
              } catch (error) {
                console.warn('Error checking payment details:', error);
                return null;
              }
            });

            const results = await Promise.all(paymentDetailsPromises);
            const validPayments = results.filter((p): p is PaymentWithRemaining => p !== null);
            
            validPayments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

            setAvailablePayments(validPayments);
          } catch (fallbackError) {
            console.error('Fallback method also failed:', fallbackError);
          }
        }
      } finally {
        setLoadingPayments(false);
      }
    };

    loadPayments();
  }, [isOpen, executorKey, siteId, siteExecutorId, userId, allocationTarget]);

  useEffect(() => {
    if (!isOpen || executorKey == null) {
      setUnpaidEarnings([]);
      setPayUnpaidEarnings(true);
      return;
    }

    const loadUnpaidEarnings = async () => {
      try {
        if (!allocationTargetReady) {
          setUnpaidEarnings([]);
          return;
        }
        const rows = await sitePaymentAllocationV2Api.getUnpaidEarningsForAllocation(siteId, allocationTarget);
        console.log('[PaymentSheet] unpaid-earnings loaded', {
          count: rows.length,
          target: allocationTarget,
          sample: rows.slice(0, 3)
        });
        setUnpaidEarnings(rows);
      } catch (error) {
        console.warn('Не удалось загрузить неоплаченные начисления', error);
        setUnpaidEarnings([]);
      }
    };

    loadUnpaidEarnings();
  }, [isOpen, executorKey, siteId, siteExecutorId, userId, allocationTarget, allocationTargetReady]);

  // Preview с сервера (фаза C2): debounced при изменении выбора
  useEffect(() => {
    if (!isOpen || !allocationTargetReady || sessions.length === 0) {
      setServerPreview(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const preview = await sitePaymentAllocationV2Api.previewShiftPayment(siteId, buildShiftPaymentRequest());
        setServerPreview(preview);
      } catch (error) {
        console.warn('Не удалось загрузить preview оплаты смен', error);
        setServerPreview(null);
      }
    }, 350);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    siteId,
    sessions,
    selectedPaymentIds,
    newPaymentAmount,
    createNewPaymentForRemaining,
    payUnpaidEarnings,
    newPaymentComment,
    userId,
    siteExecutorId,
    unpaidBonusEarningsForSelection
  ]);

  const handleTogglePayment = (paymentId: number) => {
    setSelectedPaymentIds(prev => {
      const next = new Set(prev);
      if (next.has(paymentId)) {
        next.delete(paymentId);
      } else {
        next.add(paymentId);
      }
      return next;
    });
  };

  const handleUseRecommendedTopUp = () => {
    setCreateNewPaymentForRemaining(true);
    setNewPaymentAmount('');
  };

  const handleAllocate = async () => {
    if (!userId && !siteExecutorId) return;

    const previewTarget = serverPreview?.targetAmount ?? targetAmount;
    const previewHasOps = serverPreview?.success && (
      (serverPreview.plannedSalaryAllocated ?? 0) > MIN_AMOUNT ||
      (serverPreview.plannedBonusAllocated ?? 0) > MIN_AMOUNT
    );

    if (totalAmount <= 0 && previewTarget <= MIN_AMOUNT) {
      showError('Укажите сумму для распределения');
      return;
    }

    if (!previewHasOps && activityAllocations.length === 0 && !(payUnpaidEarnings && unpaidBonusEarningsTotal > 0.01)) {
      showError('Нет сумм для оплаты');
      return;
    }

    setAllocating(true);
    try {
      const idempotencyKey = crypto.randomUUID();
      const result = await sitePaymentAllocationV2Api.allocateShiftPayment(
        siteId,
        buildShiftPaymentRequest(),
        idempotencyKey
      );

      if (!result.success) {
        showError(result.errorMessage || 'Ошибка распределения средств');
        return;
      }

      showSuccess(result.message || (sessions.length === 1 ? 'Средства успешно распределены' : `Средства успешно распределены по ${sessions.length} сменам`));
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error allocating payment:', error);
      const conflictMsg = getPaymentAllocationConflictMessage(error);
      if (conflictMsg) {
        showError(conflictMsg);
        return;
      }
      showError(error.response?.data?.message || error.response?.data?.errorMessage || 'Ошибка распределения средств');
    } finally {
      setAllocating(false);
    }
  };

  // Универсальный заголовок: для одной смены - "Оплата смены", для нескольких - "Оплата смен (N)"
  const title = useMemo(() => {
    return sessions.length === 1 ? 'Оплата смены' : `Оплата смен (${sessions.length})`;
  }, [sessions.length]);

  if (sessions.length === 0) {
    return null;
  }

  // Если смены разных пользователей - показываем ошибку
  if (!isSingleUser) {
    return (
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        size="large"
        showHandle={true}
        allowSwipeToClose={true}
        allowSwipeToExpand={false}
      >
        <div className="multiple-sessions-payment-sheet">
          <div className="error-state">
            <i className="ri-error-warning-line"></i>
            <h3>Невозможно оплатить выбранные смены</h3>
            <p>Выбранные смены принадлежат разным исполнителям. Для множественной оплаты выберите смены одного исполнителя.</p>
          </div>
        </div>
      </BottomSheet>
    );
  }

  const unpaidActivitiesCount = allUnpaidActivities.length;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="large"
      showHandle={true}
      allowSwipeToClose={true}
      allowSwipeToExpand={true}
    >
      <div className="multiple-sessions-payment-sheet">
        {/* Информация о сотруднике и сменах */}
        <div className="sessions-info">
          <h3>{userName}</h3>
          {sessions.length > 1 && <p className="sessions-count">Смен: {sessions.length}</p>}
        </div>

        {loading ? (
          <div className="loading-state">
            <i className="ri-loader-4-line ri-spin"></i>
            <span>Загрузка активностей...</span>
          </div>
        ) : (
          <>
            {/* Рекомендованная сумма */}
            {recommendedAmount > 0 && (
              <div className="recommended-amount-section">
                <div className="recommended-amount-info">
                  <span className="label">Зарплата к оплате:</span>
                  <span className="amount">{recommendedAmount.toFixed(2)} ₽</span>
                </div>
                {unpaidActivitiesCount > 0 && (
                  <p className="activities-count">
                    Неоплаченных активностей: {unpaidActivitiesCount}
                  </p>
                )}
              </div>
            )}

            {unpaidBonusEarningsTotal > 0.01 && (
              <div className="recommended-amount-section">
                <div className="recommended-amount-info">
                  <span className="label">Начисления к оплате:</span>
                  <span className="amount">{unpaidBonusEarningsTotal.toFixed(2)} ₽</span>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={payUnpaidEarnings}
                    onChange={(e) => setPayUnpaidEarnings(e.target.checked)}
                  />
                  <span>Оплатить начисления (премии, компенсации)</span>
                </label>
                {payUnpaidEarnings && unpaidBonusEarningsForSelection.length > 0 && (
                  <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                    {unpaidBonusEarningsForSelection.slice(0, 6).map((earning) => (
                      <div key={earning.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
                        <span style={{ opacity: 0.85 }}>
                          {earning.description || `${earning.earningTypeName} #${earning.id}`}
                        </span>
                        <strong>{earning.remaining.toFixed(2)} ₽</strong>
                      </div>
                    ))}
                    {unpaidBonusEarningsForSelection.length > 6 && (
                      <div style={{ fontSize: 12, opacity: 0.75 }}>
                        И еще {unpaidBonusEarningsForSelection.length - 6} начислений
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Нераспределенные средства */}
            {availablePayments.length > 0 && (
              <div className="unallocated-funds-section">
                <h4>
                  <i className="ri-money-dollar-circle-line"></i>
                  Нераспределенные средства
                </h4>
                <p className="unallocated-funds-hint">
                  Серым отмечены документы, из которых сейчас нельзя взять деньги на эту оплату: остаток ноль/меньше копейки или
                  перераспределение сверх суммы документа (отрицательный остаток). Они учитываются в общей сумме сверху, но не
                  выбираются как источник.
                </p>
                <div className="payments-list">
                  {availablePayments.map(payment => (
                    <button
                      key={payment.id}
                      type="button"
                      className={`payment-item ${selectedPaymentIds.has(payment.id) ? 'selected' : ''}`}
                      onClick={() => handleTogglePayment(payment.id)}
                      disabled={loadingPayments || payment.remainingAmount <= MIN_AMOUNT}
                      title={
                        payment.remainingAmount <= MIN_AMOUNT
                          ? payment.remainingAmount < 0
                            ? 'Перераспределено больше суммы документа — для новой оплаты этот платёж не используется'
                            : 'Нет доступного остатка для списания в эту оплату'
                          : undefined
                      }
                    >
                      <div className="payment-checkbox">
                        <i className={selectedPaymentIds.has(payment.id) ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'}></i>
                      </div>
                      <div className="payment-item-info">
                        <span className="payment-date">
                          {new Date(payment.date).toLocaleDateString('ru-RU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                        <span className="payment-remaining">
                          {payment.remainingAmount.toFixed(2)} ₽
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
                {selectedPaymentIds.size > 0 && (
                  <div className="selected-payments-summary">
                    <span>Выбрано платежей: {selectedPaymentIds.size}</span>
                    <span className="selected-amount">{selectedUnallocatedAmount.toFixed(2)} ₽</span>
                  </div>
                )}
              </div>
            )}

            {/* Ввод суммы нового платежа */}
            <div className="new-payment-section">
              <button
                type="button"
                className="session-date-header"
                onClick={() => setIsNewPaymentExpanded(prev => !prev)}
                aria-expanded={isNewPaymentExpanded}
                style={{ width: '100%', justifyContent: 'space-between' }}
              >
                <span className="session-date-label">Создать новый платеж</span>
                <i className={`ri-arrow-${isNewPaymentExpanded ? 'up' : 'down'}-s-line`} aria-hidden />
              </button>

              {isNewPaymentExpanded && (
                <>
                  <div className="amount-input-wrapper">
                    <div className="amount-input-container">
                      <input
                        id="new-payment-amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={newPaymentAmount}
                        onChange={(e) => {
                          setCreateNewPaymentForRemaining(false);
                          setNewPaymentAmount(e.target.value);
                        }}
                        placeholder={createNewPaymentForRemaining && serverPreview?.success
                          ? serverPreview.computedNewPaymentAmount.toFixed(2)
                          : '0.00'}
                        className="amount-input"
                      />
                    </div>
                    {displayRecommendedTopUp > 0.01 && (
                      <div className="amount-button-container">
                        <button
                          type="button"
                          className="use-recommended-link"
                          onClick={handleUseRecommendedTopUp}
                        >
                          ВСЁ ({displayRecommendedTopUp.toFixed(2)} ₽)
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Комментарий к новому платежу */}
                  <div className="form-group">
                    <label htmlFor="new-payment-comment">Комментарий</label>
                    <textarea
                      id="new-payment-comment"
                      value={newPaymentComment}
                      onChange={(e) => setNewPaymentComment(e.target.value)}
                      placeholder="Комментарий к платежу (необязательно)"
                      className="comment-textarea"
                      rows={2}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Итого */}
            {totalAmount > 0 && (
              <div className="total-summary">
                <div className="summary-row">
                  <span>Из нераспределенных:</span>
                  <span>{selectedUnallocatedAmount.toFixed(2)} ₽</span>
                </div>
                {(parseFloat(newPaymentAmount || '0') > 0 || (createNewPaymentForRemaining && (serverPreview?.computedNewPaymentAmount ?? 0) > 0)) && (
                  <div className="summary-row">
                    <span>Новый платеж:</span>
                    <span>
                      {(createNewPaymentForRemaining && serverPreview?.success
                        ? serverPreview.computedNewPaymentAmount
                        : parseFloat(newPaymentAmount || '0')
                      ).toFixed(2)} ₽
                    </span>
                  </div>
                )}
                <div className="summary-row total">
                  <span>Итого:</span>
                  <span>{totalAmount.toFixed(2)} ₽</span>
                </div>
              </div>
            )}

            {/* Распределение по активностям / начислениям */}
            {(activityAllocations.length > 0 || hasPremiumOnlyDistribution) && (
              <div className="allocation-preview">
                <h4>{activityAllocations.length > 0 ? 'Распределение по активностям:' : 'Распределение по начислениям:'}</h4>
                {effectiveUnallocatedAmount > 0.01 && (
                  <div className="excess-amount-warning">
                    <i className="ri-information-line"></i>
                    <span>
                      Будет распределено {plannedTotalAllocation.toFixed(2)} ₽. 
                      Нераспределенный остаток: {effectiveUnallocatedAmount.toFixed(2)} ₽.
                    </span>
                  </div>
                )}
                {activityAllocations.length > 0 && (
                  <div className="activities-list">
                    {activityAllocationsByDate.map(({ dateKey, dateLabel, activities, sessionSum }) => {
                      const isCollapsed = collapsedSessionDates.has(dateKey);
                      return (
                        <div key={dateKey} className="session-date-group">
                          <button
                            type="button"
                            className={`session-date-header ${isCollapsed ? 'collapsed' : ''}`}
                            onClick={() => toggleSessionCollapsed(dateKey)}
                            aria-expanded={!isCollapsed}
                          >
                            <i className={`ri-arrow-${isCollapsed ? 'down' : 'up'}-s-line session-date-chevron`} aria-hidden></i>
                            <span className="session-date-label">Сессия: {dateLabel}</span>
                            <span className="session-date-sum">{sessionSum.toFixed(2)} ₽</span>
                          </button>
                          {!isCollapsed && (
                            <div className="session-date-activities">
                              {activities.map(activity => (
                                <div key={`${activity.sessionId}-${activity.id}`} className="activity-allocation-item activity-allocation-item-compact">
                                  <div className="activity-line-primary">
                                    <span className="activity-name">
                                      {activity.typeOfWorkName || 'Неизвестный вид работ'}
                                    </span>
                                    <span className="activity-time">
                                      {activity.timeStart
                                        ? `${new Date(activity.timeStart).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}${activity.timeEnd ? ` – ${new Date(activity.timeEnd).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}` : ''}`
                                        : (activity.hours ?? activity.allocatedHours) != null
                                          ? `${(activity.hours ?? activity.allocatedHours)!.toFixed(1)} ч`
                                          : '—'}
                                    </span>
                                  </div>
                                  <div className="activity-line-amounts">
                                    <span className="remaining">Остаток: {activity.remainingAmount?.toFixed(2) || '0.00'} ₽</span>
                                    <span className="allocation">Будет оплачено: {activity.allocationAmount?.toFixed(2) || '0.00'} ₽</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="allocation-summary">
                  {availablePayments.length > 0 && selectedPaymentIds.size === 0 && (
                    <div className="summary-warning">
                      <i className="ri-alert-line"></i>
                      <span>Есть нераспределенные платежи, которые можно использовать</span>
                    </div>
                  )}
                  <div className="summary-row">
                    <span>Зарплата к оплате:</span>
                    <span>{recommendedAmount.toFixed(2)} ₽</span>
                  </div>
                  {payUnpaidEarnings && unpaidBonusEarningsTotal > 0.01 && (
                    <div className="summary-row">
                      <span>Начисления к оплате:</span>
                      <span>{unpaidBonusEarningsTotal.toFixed(2)} ₽</span>
                    </div>
                  )}
                  <div className="summary-row total">
                    <span>Цель к распределению:</span>
                    <span>{targetAmount.toFixed(2)} ₽</span>
                  </div>
                  <div className="summary-row">
                    <span>Будет распределено:</span>
                    <span>{plannedTotalAllocation.toFixed(2)} ₽</span>
                  </div>
                  {plannedPremiumAllocation > 0.01 && (
                    <div className="summary-row">
                      <span>На начисления:</span>
                      <span>{plannedPremiumAllocation.toFixed(2)} ₽</span>
                    </div>
                  )}
                  <div className="summary-row">
                    <span>Неоплаченный остаток:</span>
                    <span>{Math.max(0, targetAmount - plannedTotalAllocation).toFixed(2)} ₽</span>
                  </div>
                  {effectiveUnallocatedAmount > 0.01 && (
                    <div className="summary-row warning">
                      <span>Останется на платеже (аванс):</span>
                      <span>{effectiveUnallocatedAmount.toFixed(2)} ₽</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Сообщение если нет активностей для оплаты */}
            {!loading && unpaidActivitiesCount === 0 && unpaidBonusEarningsTotal <= 0.01 && (
              <div className="no-activities-message">
                <i className="ri-information-line"></i>
                <span>Все активности выбранных смен уже оплачены</span>
              </div>
            )}

            {/* Кнопка распределения */}
            {(activityAllocations.length > 0 || (payUnpaidEarnings && unpaidBonusEarningsTotal > 0.01)) && (
              <div className="actions">
                <button
                  type="button"
                  className="allocate-btn"
                  onClick={handleAllocate}
                  disabled={allocating || totalAmount <= 0}
                >
                  {allocating ? (
                    <>
                      <i className="ri-loader-4-line ri-spin"></i>
                      Распределение...
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line"></i>
                      Распределить средства
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </BottomSheet>
  );
};

export default React.memo(MultipleSessionsPaymentBottomSheet);

